import { Users, Calculator, UserCheck, Clock, Calendar, AlertCircle, Briefcase, BadgeEuro } from "lucide-react";
import { LandingHeader, LandingFooter } from "@/components/landing";
import {
  FeatureHero,
  FeatureCTA,
  RelatedFeatures,
  Breadcrumb,
  FeatureSection,
  BenefitCard,
  FormulaBlock,
  ExampleBlock,
} from "@/components/product";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const features = [
  {
    icon: UserCheck,
    title: "Fiche membre complète",
    description: "Nom, poste, spécialité, coordonnées — tout en un seul endroit.",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
  },
  {
    icon: Calculator,
    title: "Calcul automatique du coût",
    description: "Taux horaire + charges patronales = coût journalier calculé.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
  {
    icon: Clock,
    title: "Gestion des statuts",
    description: "Disponible, sur chantier, en arrêt, en congés — toujours à jour.",
    gradient: "from-[#10B981] to-[#059669]",
  },
  {
    icon: Calendar,
    title: "Gestion des absences",
    description: "Congés, arrêts maladie, indisponibilités — planifiez en conséquence.",
    gradient: "from-[#3B82F6] to-[#2563EB]",
  },
];

const teamMembers = [
  { name: "Mohamed K.", role: "Carreleur", rate: "25€/h", charges: "45%", dailyCost: "290€/j", status: "Sur chantier", statusColor: "text-[#10B981]" },
  { name: "Jean-Pierre D.", role: "Maçon", rate: "28€/h", charges: "45%", dailyCost: "325€/j", status: "Disponible", statusColor: "text-[#3B82F6]" },
  { name: "Karim B.", role: "Plombier", rate: "30€/h", charges: "45%", dailyCost: "348€/j", status: "En congés", statusColor: "text-[#F59E0B]" },
];

const EquipesFeature = () => {
  usePageMeta({ title: "Gestion équipes BTP — Affectation chantier | B8ild", description: "Gérez vos équipes et affectez-les à vos chantiers en quelques clics. Suivez les heures, coûts et productivité.", url: "https://b8ild.fr/product/equipes" });
  return (
    <div className="min-h-screen bg-[#0F172A]">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Gestion équipes BTP — Affectation chantier | B8ild",
        description: "Gérez vos équipes et affectez-les à vos chantiers en quelques clics. Suivez les heures, coûts et productivité.",
        url: "https://b8ild.fr/product/equipes",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Produit", item: "https://b8ild.fr/product" },
          { "@type": "ListItem", position: 3, name: "Gestion des équipes", item: "https://b8ild.fr/product/equipes" },
        ],
      }} />
      <LandingHeader />

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-24">
        <Breadcrumb
          items={[
            { label: "Produit", href: "/product" },
            { label: "Gestion d'équipe" },
          ]}
        />
      </div>

      {/* Hero */}
      <FeatureHero
        badge="Gestion d'équipe"
        title="Combien vous coûte réellement"
        titleHighlight="chaque membre de votre équipe ?"
        subtitle="Taux horaire, charges patronales, coût journalier — B8ild calcule tout automatiquement pour chaque chantier."
        icon={Users}
        gradient="from-[#8B5CF6] to-[#7C3AED]"
      />

      {/* What B8ild manages */}
      <FeatureSection
        title="Ce que B8ild gère pour vous"
        subtitle="Tout ce qu'il faut pour suivre vos équipes"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <BenefitCard key={index} {...feature} />
          ))}
        </div>
      </FeatureSection>

      {/* Formula */}
      <FeatureSection variant="dark" title="Formule de calcul transparente">
        <div className="max-w-3xl mx-auto space-y-8">
          <FormulaBlock
            formula="Coût journalier = Taux horaire × 8h × (1 + Charges%)"
            description="Le calcul prend en compte les charges patronales pour un coût réel"
          />

          <ExampleBlock
            title="Exemple concret"
            content="Mohamed, carreleur : 25€/h + 45% charges"
            highlight="= 290€/jour — Sur un chantier de 10 jours = 2 900€ de main d'œuvre"
          />
        </div>
      </FeatureSection>

      {/* Team Preview */}
      <FeatureSection title="Vue d'ensemble de votre équipe">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-white/[0.05] flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Mon équipe</h3>
                <button className="px-3 py-1.5 text-sm bg-[#F59E0B]/10 text-[#F59E0B] rounded-lg hover:bg-[#F59E0B]/20 transition-colors">
                  + Ajouter un membre
                </button>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.05]">
                      <th className="text-left text-xs font-medium text-white/40 uppercase tracking-wider px-6 py-3">Membre</th>
                      <th className="text-left text-xs font-medium text-white/40 uppercase tracking-wider px-6 py-3">Poste</th>
                      <th className="text-left text-xs font-medium text-white/40 uppercase tracking-wider px-6 py-3">Taux horaire</th>
                      <th className="text-left text-xs font-medium text-white/40 uppercase tracking-wider px-6 py-3">Charges</th>
                      <th className="text-left text-xs font-medium text-white/40 uppercase tracking-wider px-6 py-3">Coût / jour</th>
                      <th className="text-left text-xs font-medium text-white/40 uppercase tracking-wider px-6 py-3">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamMembers.map((member, index) => (
                      <tr key={index} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-[8px] bg-[#131318] border border-white/[0.06] flex items-center justify-center">
                              <span className="text-sm font-medium text-white/50">
                                {member.name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <span className="text-white font-medium">{member.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-white/60">{member.role}</td>
                        <td className="px-6 py-4 text-white/60">{member.rate}</td>
                        <td className="px-6 py-4 text-white/60">{member.charges}</td>
                        <td className="px-6 py-4">
                          <span className="font-semibold text-[#F59E0B]">{member.dailyCost}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-sm ${member.statusColor}`}>{member.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="px-6 py-4 bg-white/[0.02] border-t border-white/[0.05]">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/50">
                    3 membres • 2 disponibles
                  </span>
                  <span className="text-sm text-white/70">
                    Coût moyen : <span className="font-semibold text-white">321€/j</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Statuses */}
      <FeatureSection variant="dark" title="Gestion des statuts en temps réel">
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { status: "Disponible", color: "bg-[#10B981]", count: 5 },
              { status: "Sur chantier", color: "bg-[#3B82F6]", count: 8 },
              { status: "En arrêt", color: "bg-[#EF4444]", count: 1 },
              { status: "En congés", color: "bg-[#F59E0B]", count: 2 },
            ].map((item, index) => (
              <div key={index} className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-4 text-center">
                <div className={`h-3 w-3 rounded-full ${item.color} mx-auto mb-3`} />
                <p className="text-2xl font-bold text-white">{item.count}</p>
                <p className="text-sm text-white/50">{item.status}</p>
              </div>
            ))}
          </div>
        </div>
      </FeatureSection>

      {/* Pourquoi c'est important */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Votre ouvrier ne coûte pas 2 200€. Il coûte 3 700€.
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Patrick est macon, 4 ouvriers. Il paie ses gars entre 1 800 EUR et 2 400 EUR brut par mois. Mais le vrai cout ? Bien plus. Charges patronales : 42 a 45% du brut. Conges payes BTP (caisse CIBTP) : 12,5%. Mutuelle obligatoire : 40-60 EUR/mois. Intemperies (caisse BTP) : 0,68%. AT/MP : 3 a 6% selon le metier.
              </p>
              <p>
                Un ouvrier a 2 200 EUR brut coute environ 3 700 EUR/mois charges comprises. Soit 23 EUR/h sur 160h travaillees. Pas 14 EUR.
              </p>
              <p>
                Si vous mettez 14 EUR/h dans vos devis au lieu de 23 EUR, chaque chantier perd 1 400 EUR par ouvrier et par mois. Sur 4 ouvriers et 12 mois : 67 200 EUR de sous-estimation. B8ild integre le coefficient de charges reel (1.45 a 1.80 selon votre structure) dans chaque calcul.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions frequentes */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions frequentes
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Comment calculer le cout reel d'un ouvrier BTP ?",
                  a: "Salaire brut x coefficient de charges (1.45 a 1.80). Le coefficient inclut : charges patronales URSSAF, retraite, Probtp, mutuelle, conges intemperies, AT/MP. Un ouvrier N3P1 a 2 200 EUR brut coute 3 520 EUR a 3 960 EUR/mois selon votre taux AT/MP. Divisez par les heures productives (130-150h/mois, pas 160h) pour obtenir le vrai cout horaire.",
                },
                {
                  q: "Quel coefficient de charges appliquer dans le BTP ?",
                  a: "Entre 1.45 et 1.80 selon la structure. Artisan seul : ~1.45. Petite entreprise 2-5 salaries : ~1.55-1.65. Entreprise 10+ salaries avec CE et avantages : ~1.70-1.80. Le coefficient par defaut dans B8ild est 1.85, modifiable par salarie. Ce chiffre inclut les charges sociales, les conges, et les absences moyennes.",
                },
                {
                  q: "Comment integrer les charges dans un devis ?",
                  a: "Estimez les heures de travail. Multipliez par le cout horaire charge (pas le salaire brut). Ajoutez les materiaux, la sous-traitance et les frais de chantier. Appliquez votre coefficient de frais generaux (1.15 a 1.25). Puis votre marge (15 a 25%). Si votre devis est trop haut, baissez la marge — pas le cout horaire.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <FeatureCTA
        title="Maîtrisez vos coûts de main d'œuvre"
        subtitle="Chaque membre, chaque coût, clairement calculé."
      />

      {/* Related Features */}
      <RelatedFeatures currentFeatureId="equipes" />

      <LandingFooter />
    </div>
  );
};

export default EquipesFeature;
